/**
 * feat-259 — every string the auto-save footer, status and error banner put on
 * screen, in ONE place.
 *
 * Not tidiness. FE vocabulary in this app renders through constants
 * (`QUIZ_TERMS` and friends) precisely so a literal-text grep cannot return a
 * TRUE ZERO for copy that is visibly on screen —
 * `reference_fe_vocabulary_renders_through_constants_not_literals`. Five panels
 * import from here, so the strings cannot drift between item types either,
 * which is half of what AC11 asks for.
 *
 * Copy authority: `design/save-status.html` §1 (the five renderings) and
 * `design/error-states.html` (the three-part banner formula: ① what failed,
 * naming the field in the label's own words · ② the reassurance · ③ the one
 * action). Part ② is not filler — AT8 pins it, because it is the difference
 * between a banner an editor believes and one that makes them re-type into a
 * text file first.
 *
 * NEVER surfaced here, deliberately: the HTTP status, the raw server message,
 * or the retry count. An editor cannot act on any of the three. The 401 lede is
 * the one exception, and it earns it by changing what the editor should DO.
 */

export const AUTO_SAVE_COPY = {
  /** Resting, before the first write of the session. A static label, not a
   *  status — it carries the discoverability the Save button used to. */
  idle: 'Auto-saves',
  saving: 'Saving…',
  saved: 'Saved',
  /** Settled, ~2s after a write. `time` comes from `formatSaveTime`. */
  savedAtTime: (time: string) => `Saved ${time}`,
  /**
   * feat-270 panel 5 — the same line, plus four words, when the tenant's
   * curriculum approval gate is ON and the save has landed.
   *
   * `design/NOTES.md` calls this the cheapest, highest-value addition in the
   * feature, and the reason is the human's own framing: "the saves are happening
   * automatically which would sort of imply that items need to be unapproved
   * automatically at the same time." THIS is the moment the editor creates the
   * pending state, and it is the only moment they are looking. Teaching the rule
   * here is worth more than any badge.
   *
   * ⚠ NO COLOUR, and that is `SaveStatus.tsx`'s own governing rule, not a
   * preference: "colour in this footer means 'something needs you'. Spending it
   * on the outcome that fires fifty times an hour leaves nothing for the one that
   * doesn't." Four of the five renderings stay one ink at one size; only the
   * failure changes colour, and this must not become the exception.
   *
   * The `saving` and error renderings are deliberately untouched: a write that
   * has not landed has not un-approved anything yet.
   */
  savedNotLiveYet: 'not live yet',
  notSaved: 'Not saved',

  delete: 'Delete',
  tryAgain: 'Try again',
  /** AT9's escape hatch. The consequence is IN the label, not in a tooltip. */
  leaveAnyway: "Leave anyway — this change won't be saved",

  /** ② of the formula. Must be literally true — AT8 is what keeps it true. */
  valueKept: 'Your text is still here.',

  /** ① — one field. */
  couldNotSaveField: (label: string) => `Couldn't save ${label}.`,
  /** ① — two or more. One banner, never a stack: N banners in a 380px column
   *  turn a recoverable problem into a wall. The count leads and the names
   *  follow in the panel's own top-to-bottom order. */
  couldNotSaveFields: (count: number) => `Couldn't save ${count} fields:`,
  fieldList: (labels: string[]) => `${labels.join(', ')}.`,

  /** ① — the row does not exist yet. "Couldn't save Question text" would imply
   *  the item exists with one bad field, which is the wrong mental model. */
  couldNotCreate: (typeSingular: string) => `Couldn't create this ${typeSingular.toLowerCase()}.`,
  createNothingSaved: 'Nothing has been saved yet — your text is still here.',

  /** ① — 401/403. "In another tab" is deliberate: re-authenticating in THIS tab
   *  discards the very text the banner just promised was still here. */
  signInExpired: (label: string) => `Your sign-in expired, so ${label} couldn't be saved.`,
  signInAgainThen: 'Sign in again in another tab, then',

  /** State F — the banner already on screen escalates IN PLACE. Not a modal and
   *  not a `window.confirm`: a modal would hide the very text it is asking
   *  about, and the point of the warning is that you can read what you are
   *  about to lose while you decide. */
  stillUnsaved: (label: string) => `${label} still hasn't saved.`,
  wouldLeaveBehind: 'Opening another item now would leave this change behind.',
} as const;

export default AUTO_SAVE_COPY;
